import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import _get from 'lodash/get';
import { useWidgetTemplatesLazyQuery, WidgetsKindEnum } from 'graphql-common';
import { FieldItem } from '@lib/interfaces/form';
import { FormRef } from '@lib/components/ReactHookForm/types';
import { GetLoadOptions, SelectOption } from '@lib/components/Select/types';
import { getWidgetTemplatesLoadOptions } from 'utils/fetch/searchLists/widgetTemplatesSearchList';
import { ContextProps } from 'hooks/useOutletContext';
import { LocalWidget } from 'routes/Home/hooks/useWidgetEditor';
import getWidgetFormFields from './getWidgetFormFields';
import getEditWidgetDefaultValues from './getEditWidgetDefaultValues';
import getWidgetTemplateFormValues from './getWidgetTemplateFormValues';

/**
 * Original button settings from an existing widget
 */
interface OriginalButtonSettings {
  enabled?: boolean | null;
  updatedAt?: string | null;
}

/**
 * Original buttons configuration from an existing widget
 */
interface OriginalButtons {
  task?: OriginalButtonSettings | null;
  intervention?: OriginalButtonSettings | null;
  formTemplate?: OriginalButtonSettings | null;
  asset?: OriginalButtonSettings | null;
  site?: OriginalButtonSettings | null;
  teamMember?: OriginalButtonSettings | null;
}

interface UseWidgetModalFormActionsProps {
  isEditMode: boolean;
  editingWidget?: LocalWidget | null;
}

interface UseWidgetModalFormActionsReturn {
  /** Form fields configuration */
  fields: FieldItem[];
  /** Default form values */
  defaultFormValues: Record<string, unknown>;
  /** Internal form ref for accessing form methods */
  internalFormRef: React.MutableRefObject<FormRef | undefined>;
}

/**
 * Extracts original buttons configuration from a LocalWidget.
 * This is used to determine which buttons were previously enabled.
 */
function getOriginalButtons(
  editingWidget: LocalWidget | null | undefined,
): OriginalButtons | undefined {
  if (!editingWidget) return undefined;

  // Get buttons from originalWidget first (for widgets that have been edited in current session)
  // Fall back to fields.buttons for widgets loaded directly from API
  const buttons =
    (editingWidget.originalWidget?.fields?.buttons as OriginalButtons) ||
    (editingWidget.fields?.buttons as OriginalButtons);

  return buttons || undefined;
}

/**
 * Hook that handles all logic for the WidgetModalForm component.
 *
 * Responsibilities:
 * - Form fields: builds field configuration with load options
 * - Default values: computes initial form values for add/edit modes
 * - Widget change handler: prefills form when template is selected
 * - Access control: passes user access info to form fields for button visibility
 */
export default function useWidgetModalFormActions({
  isEditMode,
  editingWidget,
}: UseWidgetModalFormActionsProps): UseWidgetModalFormActionsReturn {
  const { t } = useTranslation();
  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();

  // Internal form ref to access form methods for prefilling
  const internalFormRef = useRef<FormRef | undefined>(undefined);

  const [fetchWidgetTemplatesLazyQuery] = useWidgetTemplatesLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const getWidgetTemplatesLoadOptionsHandler: GetLoadOptions = useCallback(
    () => getWidgetTemplatesLoadOptions(fetchWidgetTemplatesLazyQuery),
    [fetchWidgetTemplatesLazyQuery],
  );

  // Extract original buttons from the editing widget for access control
  const originalButtons = useMemo(
    () => getOriginalButtons(editingWidget),
    [editingWidget],
  );

  // Handle widget selection change - prefill form with template data
  const handleWidgetChange = useCallback(
    (widgetOption: SelectOption | null) => {
      if (!widgetOption || !internalFormRef.current) return;

      const templateData = _get(widgetOption, 'data') as {
        id: string;
        kind: WidgetsKindEnum;
        title?: string;
        description?: string;
        fields?: Record<string, unknown>;
        attachmentsAttached?: Array<Record<string, unknown>>;
      } | null;

      if (!templateData) return;

      // Get form values from template data
      const templateFormValues = getWidgetTemplateFormValues(templateData, t);

      // Update form values while preserving the widget selection
      Object.entries(templateFormValues).forEach(([fieldName, value]) => {
        internalFormRef.current?.setValue(fieldName, value);
      });
    },
    [t],
  );

  // Build form fields configuration
  const fields: FieldItem[] = useMemo(
    () =>
      getWidgetFormFields({
        t,
        getWidgetTemplatesLoadOptions: getWidgetTemplatesLoadOptionsHandler,
        isEditMode,
        onWidgetChange: handleWidgetChange,
        getUserAccessByComponent,
        getUserAccessByCompany,
        originalButtons,
      }),
    [
      t,
      getWidgetTemplatesLoadOptionsHandler,
      isEditMode,
      handleWidgetChange,
      getUserAccessByComponent,
      getUserAccessByCompany,
      originalButtons,
    ],
  );

  // Build default form values
  const defaultFormValues = useMemo(
    () =>
      isEditMode && editingWidget
        ? getEditWidgetDefaultValues(editingWidget, t)
        : { widget: undefined },
    [isEditMode, editingWidget, t],
  );

  return {
    fields,
    defaultFormValues,
    internalFormRef,
  };
}
